/**
 * One line per Map-step row (the compact tag editor): where the value is read
 * from and as what, e.g. "DB1 byte 0 · float32" or "holding 4 · int16".
 * Pure, so it is tested without rendering the editor.
 */

import type { DriverInfo } from "@/lib/api";
import type { TagRow } from "@/components/iot/ThingTagRows";
import { modbusPointType, rowPollSec } from "./instrument-body";
import { pointAddressLabel } from "./points";
import { validateParams } from "./schema-form";

export interface TagRowSummary {
  /** "DB1 byte 0", "holding 4", "no address". */
  address: string;
  /** The type the point is decoded as ("float32", "int16", "bool"). */
  type: string;
  /** Something the operator must fix before going live (shown on the line, and opens the row). */
  problem: string | null;
}

export function tagRowSummary(row: TagRow, pointDriver?: DriverInfo): TagRowSummary {
  // Each point is sent its row's poll; one the platform would refuse falls back to the instrument's, so say so.
  const pollProblem = row.poll_sec.trim() !== "" && rowPollSec(row) === undefined ? "Poll: 1 to 86400 s." : null;
  if (pointDriver) {
    const address = row.point_address;
    const check = validateParams(pointDriver.address_schema, address ?? {});
    const empty = !address || Object.keys(address).length === 0;
    return {
      address: pointAddressLabel(address),
      type: row.point_type ?? "int16",
      problem: empty ? "Set its address." : check.valid ? pollProblem : "Its address is not valid.",
    };
  }
  const space = row.register_kind ?? "holding";
  const reg = row.address.trim();
  return {
    address: reg ? `${space} ${reg}` : "no address",
    type: modbusPointType(row),
    problem: reg ? pollProblem : "Set its register.",
  };
}
